/**
 * Global mixin to handle closing modals/dialogs when pressing Esc key
 * 
 * This mixin automatically adds close functionality for components that have:
 * - Prop or data: isDisplay, dialog, visible, showDialog, show
 * - Method: closeTool, closeDialog, close, hide
 * 
 * Usage: This mixin is applied globally, no need to import in individual components
 */

// Global variable to track which modal is currently active
let _currentActiveModal = null

export default {
  data() {
    return {
      _escHandlerActive: false,
      _escHandlerAttached: false,
      _isCurrentActiveModal: false
    }
  },
  
  mounted() {
    // Initialize mixin data if not already done
    if (typeof this._escHandlerActive === 'undefined') {
      this._escHandlerActive = false;
    }
    if (typeof this._escHandlerAttached === 'undefined') {
      this._escHandlerAttached = false;
    }
    if (typeof this._isCurrentActiveModal === 'undefined') {
      this._isCurrentActiveModal = false;
    }
    
    this._setupEscWatcher()
  },
  
  beforeDestroy() {
    this._cleanupEscHandler()
  },
  
  methods: {
    /**
     * Setup watcher to monitor when modal is opened
     */
    _setupEscWatcher() {
      // ONLY track states that are VERIFIED as modal/dialog
      const modalStates = [
        'dialog',           // Generic dialog state (v-dialog v-model) - MUST have closeDialog() method
        'isDisplay',        // Modal tool
        'showDialog',       // Dialog with prefix
        'isOpen',           // Modal with prefix  
        'isShow',           // Modal with prefix
        'isRename',         // Specific modal action
        'isEdit',           // Specific modal action
        'isCreate',         // Specific modal action
        'isModal',          // Explicit modal flag
        'showModal',        // Modal with prefix
        'opened',           // Modal state
        'dialogVisible',    // Dialog visible
        'popupProperty',    // Specific popup
        'isConfig',         // Config dialog
        'isCustomP2P98',    // Custom dialog
        'rename_dialog',    // Specific dialog
        'dialogStyle',      // Style dialog
        'showEditOption',   // Edit option dialog
        'isShowDialog',     // Dialog with prefix
        'showEditDialog',   // Edit dialog
        'isSave',           // Save dialog
        'popup',            // Popup
        'dialogTypeResult', // Type result dialog
        'dialogRun',        // Run dialog (nested dialog in reclass-toolset)
        'dialogClassify',   // Classify dialog (nested dialog in reclass-toolset)
        'dialogUpload',     // Upload dialog
        'isCopy',           // Copy dialog
        'isRemoveAOI',      // Remove AOI dialog
        'showDetail',       // Detail dialog
        'isMakeCopy',       // Make copy AOI dialog
        'info'              // Image detail dialog
      ]
      
      // Create watcher for each modal state
      modalStates.forEach(state => {
        if (state in this) {
          this.$watch(state, (newVal, oldVal) => {
            // ONLY process when value CHANGES (not initial value)
            if (newVal === oldVal) return
            
            // Check if ANY modal is currently open
            if (this._isModalVisible()) {
              // Modal is open -> add handler
              this._addEscHandler()
            } else {
              // All modals closed -> remove handler
              this._removeEscHandler()
            }
          })
        }
      })
      
      // Check IMMEDIATELY after setup: if modal is already open, add handler
      this.$nextTick(() => {
        if (this._isModalVisible()) {
          this._addEscHandler()
        }
      })
    },
    
    /**
     * Add event listener for Esc key
     * ONLY add when a modal is open
     */
    _addEscHandler() {
      if (!this._escHandlerAttached) {
        this._escKeyHandler = this._handleEscKey.bind(this)
        window.addEventListener('keydown', this._escKeyHandler)
        this._escHandlerAttached = true
        
        // Mark this component as the active modal
        _currentActiveModal = this
        this._isCurrentActiveModal = true
      }
    },
    
    /**
     * Remove event listener
     */
    _removeEscHandler() {
      if (this._escHandlerAttached && this._escKeyHandler) {
        window.removeEventListener('keydown', this._escKeyHandler)
        this._escHandlerAttached = false
        this._escKeyHandler = null
        
        // Clear active modal if this is the active modal
        if (this._isCurrentActiveModal) {
          _currentActiveModal = null
          this._isCurrentActiveModal = false
        }
      }
    },
    
    /**
     * Cleanup when component is destroyed
     */
    _cleanupEscHandler() {
      this._removeEscHandler()
    },
    
    /**
     * Handle event when Esc key is pressed
     */
    _handleEscKey(event) {
      if (event.key !== 'Escape') return
      
      // CRITICAL: ONLY process if this component is the active modal
      // This prevents multiple handlers from running simultaneously
      if (!this._isCurrentActiveModal || _currentActiveModal !== this) {
        // NOT the active modal -> SKIP COMPLETELY
        return
      }
      
      // Check if modal is actually open
      if (!this._isModalVisible()) {
        // Modal already closed -> DO NOT intervene
        return
      }
      
      // Find method to close modal
      const closeMethod = this._getCloseMethod()
      const modalState = this._getActiveModalState()
      
      // ONLY process if there's a way to close the modal
      if (closeMethod || modalState) {
        // Close modal FIRST
        if (closeMethod) {
          this[closeMethod]()
        } else if (modalState) {
          this[modalState] = false
        }
        
        // THEN preventDefault/stopPropagation
        event.preventDefault()
        event.stopPropagation()
      }
    },
    
    /**
     * Check if component has state to manage modal
     */
    _hasModalState() {
      const modalStates = [
        'dialog',
        'isDisplay',
        'showDialog',
        'isOpen',
        'isShow',
        'isRename',
        'isEdit',
        'isCreate',
        'isModal',
        'showModal',
        'opened',
        'dialogVisible',
        'popupProperty',
        'isConfig',
        'isCustomP2P98',
        'rename_dialog',
        'dialogStyle',
        'showEditOption',
        'isShowDialog',
        'showEditDialog',
        'isSave',
        'popup',
        'dialogTypeResult',
        'dialogRun',
        'dialogClassify',
        'dialogUpload',
        'isCopy',
        'isRemoveAOI',
        'showDetail',
        'isMakeCopy',
        'info'
      ]
      
      return modalStates.some(state => state in this)
    },
    
    /**
     * Check if modal is currently visible
     */
    _isModalVisible() {
      const modalStates = [
        'dialog',
        'isDisplay',
        'showDialog',
        'isOpen',
        'isShow',
        'isRename',
        'isEdit',
        'isCreate',
        'isModal',
        'showModal',
        'opened',
        'dialogVisible',
        'popupProperty',
        'isConfig',
        'isCustomP2P98',
        'rename_dialog',
        'dialogStyle',
        'showEditOption',
        'isShowDialog',
        'showEditDialog',
        'isSave',
        'popup',
        'dialogTypeResult',
        'dialogRun',
        'dialogClassify',
        'dialogUpload',
        'isCopy',
        'isRemoveAOI',
        'showDetail',
        'isMakeCopy',
        'info'
      ]
      
      for (const state of modalStates) {
        if (state in this && this[state] === true) {
          return true
        }
      }
      
      return false
    },
    
    /**
     * Find method to close modal
     */
    _getCloseMethod() {
      const closeMethods = [
        'closeTool',
        'closeDialog',
        'closePopup',
        'closePopupProperties',
        'closeFeatureForm',
        'closeDetection',
        'closeClassify',      // Classify dialog close method
        'closeDialogRun',     // Run dialog close method
        'closeCopyDialog',    // Copy dialog close method
        'closeConfig',        // Config dialog close method (training.vue)
        'close',
        'hide',
        'onClose',
        'handleClose',
        'cancel',
        'dismiss'
      ]
      
      for (const method of closeMethods) {
        if (typeof this[method] === 'function') {
          return method
        }
      }
      
      return null
    },
    
    /**
     * Get the currently active modal state (with value true)
     */
    _getActiveModalState() {
      const modalStates = [
        'dialog',
        'isDisplay',
        'showDialog',
        'isOpen',
        'isShow',
        'isRename',
        'isEdit',
        'isCreate',
        'isModal',
        'showModal',
        'opened',
        'dialogVisible',
        'popupProperty',
        'isConfig',
        'isCustomP2P98',
        'rename_dialog',
        'dialogStyle',
        'showEditOption',
        'isShowDialog',
        'showEditDialog',
        'isSave',
        'popup',
        'dialogTypeResult',
        'dialogRun',
        'dialogClassify',
        'dialogUpload',
        'isCopy',
        'isRemoveAOI',
        'showDetail',
        'isMakeCopy',
        'info'
      ]
      
      for (const state of modalStates) {
        if (state in this && this[state] === true) {
          return state
        }
      }
      
      return null
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/closeOnEscMixin.js